// biography-chronik.ts — „Chronik": die automatisch verdichtete Lebens-Chronik
// (Monats-Kapitel + letzte Tages-Digests). Frueher als read-only Block in den
// Einstellungen geparkt; seit 2026-06-29 (Martin) ein eigener Reiter in
// „Mein Leben", weil es sein interessantester Inhalt ist und dorthin gehoert,
// nicht zwischen die Schalter. Quelle: /api/biography/monthly + /daily.

import { renderMarkdown } from "./markdown.js";
import { mdToSpeech } from "./doc-narration.js";

interface MonthlyEntry {
  id: string;
  title: string;
  month: string; // YYYY-MM
  content: string;
  createdAt: string;
}
interface DailyEntry {
  id: string;
  date: string; // YYYY-MM-DD
  content: string;
  createdAt: string;
}

/**
 * Baut die „Chronik"-Ansicht: oben die Monats-Kapitel (neueste aufgeklappt),
 * darunter die letzten 7 Tages-Digests. Laedt beide Quellen parallel und zeigt
 * je eigene Leer-/Fehlerzustaende.
 *
 * ## Vorlesen (08.09.2026)
 *
 * Martin: *„Grundsaetzlich lese ich nichts und lasse mir die Dinge
 * vorlesen."* Die Chronik ist sein laengster zusammenhaengender Text — und
 * war bis heute ein reines LESE-Fragment.
 *
 * ⚠️ EIN Player fuer die ganze Seite, mit Abschnitten: jeder Monat und jeder
 *    Tag ist einer, und die Blaetter-Knoepfe springen dazwischen. Ein Player
 *    je Karte waere ein Dutzend Leisten auf einer Seite — und die
 *    Abspielleiste ueber der Fusszeile wuesste bei jedem neu, was laeuft.
 */
/**
 * Ein Tagesdatum, wie es auf der Seite steht.
 *
 * ⚠️ 08.09.2026 herausgezogen: Die Karten schrieben „So., 16. Aug.", der
 *    Vorlese-Player daneben „2026-08-16" — dasselbe Datum in zwei
 *    Schreibweisen auf einem Bildschirm. Wer nach Gehoer blaettert, hoert
 *    ohnehin nur den Titel; er soll derselbe sein.
 */
export function tagesDatum(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-AT", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  } catch {
    return iso;
  }
}

/**
 * Die Abschnitte fuers Vorlesen — Monate und Tage in Leserichtung.
 *
 * ⚠️ Erst wenn BEIDE Quellen da sind. Sonst stuende nach dem ersten Laden
 *    eine halbe Liste im Player, und der zweite Aufruf ueberschriebe sie —
 *    Martin haette je nach Netz mal die Monate, mal die Tage.
 */
export function chronikAbschnitte(
  monate: Array<{ title: string; content: string }>,
  tage: Array<{ date: string; content: string }>,
  sauber: (md: string) => string,
): Array<{ title: string; text: string }> {
  const raus: Array<{ title: string; text: string }> = [];
  for (const m of monate) {
    const t = sauber(m.content).trim();
    if (t) raus.push({ title: m.title, text: t });
  }
  for (const d of tage) {
    const t = sauber(d.content).trim();
    if (t) raus.push({ title: tagesDatum(d.date), text: t });
  }
  return raus;
}

/** Was der geteilte Player koennen muss, damit die Chronik ihn nutzen kann. */
interface ChronikSpieler {
  el: HTMLElement;
  setSections?: (items: Array<{ title: string; text: string }>) => void;
  setReady?: (ok: boolean) => void;
}

export function buildBiographyChronik(opts?: {
  /**
   * Der geteilte Vorlese-Player. Wird von app.ts hereingereicht, weil
   * `buildNarrationPlayer` dort lebt — dasselbe Muster wie bei Projekten,
   * Projekt-Dateien und dem Fund des Tages.
   */
  buildPlayer?: (o: {
    endpoint: string;
    getBody: (voiceId: string) => Record<string, unknown> | null;
    titel?: string;
    sectionable?: boolean;
  }) => ChronikSpieler;
}): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "book-chronik";

  /** Was vorgelesen werden kann — gefuellt, sobald beide Quellen da sind. */
  let abschnitte: Array<{ title: string; text: string }> = [];
  let monateDa: MonthlyEntry[] | null = null;
  let tageDa: DailyEntry[] | null = null;

  /*
   * ⚠️ Die Leiste steht GANZ OBEN, vor der Einleitung. Wer nicht liest,
   *    soll sie nicht unter dem Text suchen muessen (dieselbe Anordnung wie
   *    im Fund des Tages).
   */
  let spieler: ChronikSpieler | null = null;
  if (opts?.buildPlayer) {
    spieler = opts.buildPlayer({
      endpoint: "/api/tts/prepare",
      titel: "Chronik",
      sectionable: true,
      getBody: (voiceId) =>
        abschnitte.length > 0
          ? { text: abschnitte[0]!.text, voiceId }
          : null,
    });
    const reihe = document.createElement("div");
    reihe.className = "chronik-vorlesen";
    reihe.appendChild(spieler.el);
    wrap.appendChild(reihe);
  }

  const intro = document.createElement("p");
  intro.className = "book-chronik-intro";
  intro.textContent =
    "Deine Gespraeche und Aufnahmen, automatisch verdichtet: oben die " +
    "Monats-Kapitel, darunter die letzten Tage. Privater Bereich \u2014 nur du " +
    "siehst sie.";
  wrap.appendChild(intro);

  const monthlyHead = document.createElement("h4");
  monthlyHead.className = "biography-daily-head";
  monthlyHead.textContent = "Monate";
  wrap.appendChild(monthlyHead);

  const monthlyWrap = document.createElement("div");
  monthlyWrap.className = "biography-monthly-list";
  wrap.appendChild(monthlyWrap);

  const dailyHead = document.createElement("h4");
  dailyHead.className = "biography-daily-head";
  dailyHead.textContent = "Letzte Tage";
  wrap.appendChild(dailyHead);

  const dailyWrap = document.createElement("div");
  dailyWrap.className = "biography-daily-list";
  wrap.appendChild(dailyWrap);

  const statusEl = document.createElement("div");
  statusEl.className = "settings-inline-status";
  wrap.appendChild(statusEl);

  const renderMonthlyEmpty = (): void => {
    while (monthlyWrap.firstChild) {
      monthlyWrap.removeChild(monthlyWrap.firstChild);
    }
    const empty = document.createElement("div");
    empty.className = "biography-empty";
    empty.textContent =
      "Noch keine Monats-Biografie. Wird automatisch erzeugt nach " +
      "mindestens 7 Tagen mit Gespraechen.";
    monthlyWrap.appendChild(empty);
  };

  const renderMonthly = (list: MonthlyEntry[]): void => {
    while (monthlyWrap.firstChild) {
      monthlyWrap.removeChild(monthlyWrap.firstChild);
    }
    if (list.length === 0) {
      renderMonthlyEmpty();
      return;
    }
    const sorted = [...list].sort((a, b) => b.month.localeCompare(a.month));
    sorted.forEach((entry, idx) => {
      const card = document.createElement("details");
      card.className = "biography-month-card";
      if (idx === 0) card.open = true;
      const sum = document.createElement("summary");
      sum.className = "biography-month-summary";
      const title = document.createElement("span");
      title.className = "biography-month-title";
      title.textContent = entry.title;
      sum.appendChild(title);
      const meta = document.createElement("span");
      meta.className = "biography-month-meta";
      meta.textContent = new Date(entry.createdAt).toLocaleDateString("de-AT");
      sum.appendChild(meta);
      card.appendChild(sum);
      const body = document.createElement("div");
      body.className = "biography-month-body";
      renderMarkdown(body, entry.content);
      card.appendChild(body);
      monthlyWrap.appendChild(card);
    });
  };

  const renderDailyEmpty = (): void => {
    while (dailyWrap.firstChild) dailyWrap.removeChild(dailyWrap.firstChild);
    const empty = document.createElement("div");
    empty.className = "biography-empty";
    empty.textContent = "Noch keine Tages-Digests.";
    dailyWrap.appendChild(empty);
  };

  const renderDaily = (list: DailyEntry[]): void => {
    while (dailyWrap.firstChild) dailyWrap.removeChild(dailyWrap.firstChild);
    if (list.length === 0) {
      renderDailyEmpty();
      return;
    }
    const recent = list.slice(0, 7);
    for (const entry of recent) {
      const card = document.createElement("details");
      card.className = "biography-day-card";
      const sum = document.createElement("summary");
      sum.className = "biography-day-summary";
      const dateLabel = document.createElement("span");
      dateLabel.className = "biography-day-date";
      dateLabel.textContent = tagesDatum(entry.date);
      sum.appendChild(dateLabel);
      const snippet = document.createElement("span");
      snippet.className = "biography-day-snippet";
      snippet.textContent = entry.content
        .replace(/[#*_`]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 80);
      sum.appendChild(snippet);
      card.appendChild(sum);
      const body = document.createElement("div");
      body.className = "biography-day-body";
      renderMarkdown(body, entry.content);
      card.appendChild(body);
      dailyWrap.appendChild(card);
    }
  };

  const setzeAbschnitte = (): void => {
    if (!spieler || monateDa === null || tageDa === null) return;
    abschnitte = chronikAbschnitte(monateDa, tageDa, mdToSpeech);
    spieler.setSections?.(abschnitte);
    spieler.setReady?.(abschnitte.length > 0);
  };

  void (async () => {
    try {
      const r = await fetch("/api/biography/monthly", {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = (await r.json()) as { biographies?: MonthlyEntry[] };
      monateDa = j.biographies ?? [];
      renderMonthly(monateDa);
      setzeAbschnitte();
    } catch (err) {
      monateDa = [];
      setzeAbschnitte();
      renderMonthlyEmpty();
      statusEl.textContent = `Monats-Biografie laden fehlgeschlagen: ${
        err instanceof Error ? err.message : String(err)
      }`;
      statusEl.dataset.kind = "error";
    }
  })();

  void (async () => {
    try {
      const r = await fetch("/api/biography/daily?limit=7", {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = (await r.json()) as { digests?: DailyEntry[] };
      tageDa = j.digests ?? [];
      renderDaily(tageDa);
      setzeAbschnitte();
    } catch (err) {
      tageDa = [];
      setzeAbschnitte();
      renderDailyEmpty();
      statusEl.textContent = `Tages-Digests laden fehlgeschlagen: ${
        err instanceof Error ? err.message : String(err)
      }`;
      statusEl.dataset.kind = "error";
    }
  })();

  return wrap;
}
